import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ArrowLeft, ShoppingCart, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { CATEGORY_META } from "../lib/categoryMeta";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        if (p.sizes && p.sizes.length > 0) setSize(p.sizes[0]);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl mb-4">Product not found</h1>
        <Link to="/shop" className="pw-btn">Back to Shop</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center font-display text-2xl" data-testid="product-loading">
        Loading...
      </div>
    );
  }

  const onAdd = () => {
    if (product.sizes && product.sizes.length > 0 && !size) {
      toast.error("Please select a size");
      return;
    }
    addItem(product, size, qty);
    toast.success(`Added ${product.name} to cart`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="product-detail-page">
      <Seo
        title={product.name}
        path={`/product/${product.id}`}
        description={`${product.description} Shop the ${product.name} from Anthony's Playworld — ${product.category.toLowerCase()} in bright, kid-tested colors. $${product.price.toFixed(2)}, free US shipping over $50.`}
        image={product.image}
        type="product"
      />
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 mb-8 font-bold hover:underline"
        data-testid="back-button"
      >
        <ArrowLeft strokeWidth={3} size={20} /> Back
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
        <div
          className="pw-card p-4"
          style={{ background: "#FFD500" }}
        >
          <div className="aspect-square overflow-hidden rounded-lg border-3 border-black" style={{ borderWidth: 3 }}>
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover mix-blend-multiply"
              data-testid="product-image"
            />
          </div>
        </div>

        <div>
          <span className="pw-badge pink mb-3 inline-block uppercase">
            {product.category}
          </span>
          <h1
            className="font-display text-4xl sm:text-5xl uppercase leading-none"
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <p
            className="mt-4 font-display text-3xl bg-[#FFD500] inline-block border-3 border-black px-3 py-1 rounded-md"
            style={{ borderWidth: 3 }}
            data-testid="product-detail-price"
          >
            ${product.price.toFixed(2)}
          </p>
          <p className="mt-6 text-lg leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          <div className="mt-6 space-y-2 text-sm">
            <p><strong className="uppercase tracking-wider text-xs">Category:</strong> {product.category}</p>
            <p><strong className="uppercase tracking-wider text-xs">Ships from:</strong> USA — free standard shipping on orders over $50.</p>
            <p><strong className="uppercase tracking-wider text-xs">Returns:</strong> Easy 30-day returns on unused items in original packaging.</p>
            {product.sizes && product.sizes.length > 0 && (
              <p><strong className="uppercase tracking-wider text-xs">Sizes available:</strong> {product.sizes.join(", ")}</p>
            )}
          </div>

          <div className="mt-6 pw-card p-4 bg-[#FFFDF0] text-sm leading-relaxed">
            <p className="font-display uppercase text-base mb-2">Why fans love it</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Officially licensed Anthony&apos;s Playworld design</li>
              <li>Kid-tested, adventure-approved comfort</li>
              <li>Bright, fade-resistant colors that stand up to real play</li>
              <li>Printed in small batches — every piece feels special</li>
            </ul>
          </div>

          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-8" data-testid="size-selector">
              <p className="font-bold uppercase tracking-wide mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`pw-pill ${size === s ? "active" : ""}`}
                    data-testid={`size-option-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div
              className="inline-flex items-center border-3 border-black rounded-full overflow-hidden bg-white"
              style={{ borderWidth: 3 }}
            >
              <button
                className="px-3 py-2 hover:bg-[#FFD500]"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease"
                aria-label="Decrease quantity"
              >
                <Minus size={16} strokeWidth={3} />
              </button>
              <span className="px-4 font-bold" data-testid="qty-value">{qty}</span>
              <button
                className="px-3 py-2 hover:bg-[#FFD500]"
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-increase"
                aria-label="Increase quantity"
              >
                <Plus size={16} strokeWidth={3} />
              </button>
            </div>
            <button
              onClick={onAdd}
              className="pw-btn"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart size={18} strokeWidth={3} className="mr-2" />
              Add to Cart
            </button>
          </div>
        </div>
      </div>

      {/* Rich category-specific content for SEO word count */}
      {CATEGORY_META[product.category] && (
        <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="pw-card p-6 md:col-span-3">
            <h2 className="font-display text-2xl uppercase mb-3">
              About Playworld {product.category}
            </h2>
            <p className="leading-relaxed">{CATEGORY_META[product.category].intro}</p>
          </div>
          <div className="pw-card p-6" style={{ background: "#FFD500" }}>
            <p className="font-display uppercase text-lg mb-2">Materials</p>
            <p className="text-sm leading-relaxed">{CATEGORY_META[product.category].materials}</p>
          </div>
          <div className="pw-card p-6" style={{ background: "#00E5FF" }}>
            <p className="font-display uppercase text-lg mb-2">Care</p>
            <p className="text-sm leading-relaxed">{CATEGORY_META[product.category].care}</p>
          </div>
          <div className="pw-card p-6" style={{ background: "#FF2E93", color: "#fff" }}>
            <p className="font-display uppercase text-lg mb-2">Fit & Size</p>
            <p className="text-sm leading-relaxed">{CATEGORY_META[product.category].fit}</p>
          </div>
        </section>
      )}

      <section className="mt-10 pw-card p-6 sm:p-8">
        <h2 className="font-display text-2xl uppercase mb-3">Shipping & Returns</h2>
        <p className="mb-2 text-sm leading-relaxed">
          We pack and ship every Anthony&apos;s Playworld order from our US warehouse
          within one to two business days. Standard shipping typically arrives in
          three to seven business days, and orders over $50 ship free anywhere in
          the continental US. Need it faster? Expedited options are available at
          checkout for a small upgrade fee.
        </p>
        <p className="text-sm leading-relaxed">
          Not quite the right fit or vibe? No worries. We offer easy 30-day
          returns on unused items in their original packaging — just drop us a
          message from your order confirmation email and we&apos;ll help you swap
          or refund. Kids grow fast, so we get it.
        </p>
      </section>
    </div>
  );
}
